import { type ColDef, type ValueFormatterParams, type ValueGetterParams } from 'ag-grid-community';

type GridRow = Record<string, unknown>;

export interface GridExportOptions {
  actionsHeaderLabel?: string;
}

export interface GridExportData {
  headers: Array<string>;
  records: Array<Record<string, unknown>>;
}

const getNestedValue = (row: GridRow, field: string): unknown => {
  const properties = field.split('.');
  let value: unknown = row;

  for (const property of properties) {
    if (!value || typeof value !== 'object') {
      return '';
    }

    if (!Object.prototype.hasOwnProperty.call(value, property)) {
      return '';
    }

    value = (value as GridRow)[property];
  }

  return value;
};

const isExportableColumn = (columnDef: ColDef, options: GridExportOptions): boolean => {
  const isActionColumn =
    columnDef.colId === 'actions' ||
    (options.actionsHeaderLabel !== undefined &&
      columnDef.headerName === options.actionsHeaderLabel);
  const hasExportableValue =
    Boolean(columnDef.field) ||
    typeof columnDef.valueGetter === 'function' ||
    typeof columnDef.valueFormatter === 'function';

  return !isActionColumn && !columnDef.checkboxSelection && hasExportableValue;
};

const resolveColumnValue = (row: GridRow, columnDef: ColDef): unknown => {
  if (typeof columnDef.valueGetter === 'function') {
    return columnDef.valueGetter({ data: row } as ValueGetterParams);
  }

  return columnDef.field ? getNestedValue(row, columnDef.field) : '';
};

const formatColumnValue = (row: GridRow, columnDef: ColDef, value: unknown): unknown => {
  if (typeof columnDef.valueFormatter !== 'function') {
    return value;
  }

  return columnDef.valueFormatter({ value, data: row } as ValueFormatterParams);
};

export const createGridExportData = (
  columnDefs: Array<ColDef>,
  rows: Array<GridRow>,
  options: GridExportOptions = {}
): GridExportData => {
  const exportableColumns = columnDefs.filter((columnDef) =>
    isExportableColumn(columnDef, options)
  );

  return {
    headers: exportableColumns.map((columnDef) => columnDef.headerName ?? ''),
    records: rows.map((row) =>
      exportableColumns.reduce<Record<string, unknown>>((record, columnDef, index) => {
        const value = resolveColumnValue(row, columnDef);
        record[`column${index}`] = formatColumnValue(row, columnDef, value);
        return record;
      }, {})
    )
  };
};
